import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GameCard } from "@/components/GameCard";
import PairingTab from "@/components/PairingTab";
import PositionPairingTab from "@/components/PositionPairingTab";
import LiveTrackerTab from "@/components/LiveTrackerTab";
import GoalieOriginTab from "@/components/GoalieOriginTab";
import NumerologyTab from "@/components/NumerologyTab";
import OfficialCardTab from "@/components/OfficialCardTab";
import GameLinesTab from "@/components/GameLinesTab";
import SkaterStatsTab from "@/components/SkaterStatsTab";
import ArchetypeHitRates from "@/components/ArchetypeHitRates";
import AuditTab from "@/components/AuditTab";
import GreenBallTab from "@/components/GreenBallTab";
import FirstBucketTab from "@/components/FirstBucketTab";
import TonightHome from "@/components/TonightHome";
import { PageHeader } from "@/components/PageHeader";
import { useSlateSummary } from "@/lib/slateSummary";
import { PlayerCardProvider } from "@/components/PlayerCardDialog";
import AIAssistant from "@/components/AIAssistant";
import { toApiDate, toInputDate, todayInput, shiftDate, dateLabel } from "@/lib/format";
import { RefreshCw, ChevronLeft, ChevronRight, CalendarDays, Loader2, WifiOff, LogOut, Users, Crosshair, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useTracker } from "@/lib/tracker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NUMFMT = (n) => (n == null ? "—" : Number(n).toLocaleString());


const HERO_IMG = "https://customer-assets-agu9un31.emergentagent.net/job_shot-matchup/artifacts/xmu31y3r_E073E870-2719-44C5-AB31-B0773B0FD372.png";

const SECTIONS = [
  { title: "Tonight", items: [
    { key: "home", label: "HOME", icon: "🏠", desc: "Tonight at a glance: goalies, best picks, best value odds, alerts and last night's audit." },
    { key: "targets", label: "TARGETS", icon: "🎯", desc: "Every game on the slate. Open a game for its picks, matchup and odds.",
      legend: [["Picks", "Goal Origin targets and Game Trends Top 3s"], ["Matchup", "Team performance, positional weakness, SOG allowed, lineups, history"], ["Odds", "Anytime goal prices with our model's VALUE flag"]] },
    { key: "official", label: "OFFICIAL CARD", icon: "✅", desc: "The approved betting card for tonight, graded live as goals happen.",
      legend: [["APPROVED", "CYPHER score ≥ 70 with an exact line match"], ["WATCH", "CYPHER score ≥ 55"], ["ATG", "Best anytime-goal price; green VALUE = model beats implied odds by 3+ pts"]] },
    { key: "live", label: "LIVE TRACKER", icon: "📡", desc: "Goals as they happen, matched against our picks." },
  ] },
  { title: "Build", items: [
    { key: "pairing", label: "PAIRING", icon: "🤝", desc: "Same-unit and cross-game player pairs built from position matchups." },
    { key: "archetypes", label: "ARCHETYPES", icon: "🧬", desc: "Archetype hit rates and the CYPHER pairing engine.",
      legend: [["Archetype", "One primary label per skater from his last 5 games"], ["Hit %", "Share of that archetype's players who scored"], ["Fit", "How well two archetypes combine (creator + finisher = 100)"]] },
    { key: "goalie", label: "GOALIE ORIGIN", icon: "🥅", desc: "Where each starting goalie has been giving up goals, and who to target." },
    { key: "numerology", label: "NUMEROLOGY", icon: "🔢", desc: "PlayEmStr8 numerology for tonight's date matched to jersey numbers." },
  ] },
  { title: "Market & Stats", items: [
    { key: "lines", label: "GAME LINES", icon: "🧊", desc: "Anytime goal odds from 10 books, plus moneyline, puck line and totals.",
      legend: [["Best", "Highest price across all books"], ["Impl %", "Win chance implied by the best price"], ["Model %", "Our goal chance from goal rate × CYPHER matchup"], ["Edge", "Model % minus implied %; VALUE at +3 or more"]] },
    { key: "skaters", label: "SKATER STATS", icon: "📊", desc: "Full skater table with archetypes. Tap a name for the player card.",
      legend: [["G/GP", "Goals per game"], ["L5", "Last 5 team games"], ["TONIGHT", "Player's team plays tonight"]] },
  ] },
  { title: "Green Ball · Tennis", items: [
    { key: "tennis", label: "GREEN BALL", icon: "🎾", desc: "ATP & WTA match center: Matchup → Form → Surface → Risk → Fusion → Official Card.",
      legend: [["APPROVED", "Every gate passed: data, matchup, form, surface, risk not HIGH, fusion ≥ 60%"], ["WATCH", "Fusion ≥ 60% and risk not HIGH, but a form or surface gate is missing"], ["DATA UNAVAILABLE", "Fewer than 8 stored matches for a player, or surface unknown"], ["TG", "Projected total games from both players' recent matches"]] },
  ] },
  { title: "First Bucket · NBA", items: [
    { key: "nba", label: "FIRST BUCKET", icon: "🏀", desc: "NBA first basket: Tip-Off → Player history → Opponent weakness → Lineup → Opening play → Fusion → Official Card.",
      legend: [["Fusion", "0-100 ranking index, not a probability"], ["Est. prob", "Shown only when the walk-forward backtest is calibrated"], ["CONFIRMED / EXPECTED / PROJECTED", "Starter status: box score or RotoWire confirmed, RotoWire expected, or recent starters"], ["APPROVED", "Every mandatory gate passes, Fusion ≥ 90, coverage ≥ 80%"], ["WATCH / PASS / DATA BLOCKED", "Fusion 60-79.9 or recoverable gap / Fusion < 60 / unverifiable data"]] },
  ] },
  { title: "System", items: [
    { key: "audit", label: "AUDIT", icon: "🧪", desc: "How accurate every engine has been, and how the model is learning.",
      legend: [["Hit %", "Share of picks that scored (first-goal / multi lists use those outcomes)"], ["Calibration", "Bars = actual goal %, line = what the model predicted"], ["Weights", "Auto-tuned nightly, max ±5 pts per week"]] },
    { key: "tracker", label: "TRACKER", icon: "📌", desc: "" },
  ] },
];
const NAV = SECTIONS.flatMap((x) => x.items);
const NAV_META = Object.fromEntries(NAV.map((n) => [n.key, n]));

const NavButton = ({ n, active, badge, onClick, compact }) => (
  <button
    data-testid={`${compact ? "mnav" : "nav"}-${n.key}`}
    onClick={onClick}
    className={`flex min-h-[40px] shrink-0 items-center gap-2 rounded-lg px-3 font-mono text-[11px] font-bold uppercase tracking-wider transition-colors ${compact ? "" : "w-full"} ${
      active ? "bg-cyan-500/15 text-cyan-300 shadow-[inset_0_0_0_1px_rgba(34,211,238,0.45)]" : "text-slate-400 hover:bg-slate-900 hover:text-slate-200"}`}
  >
    <span>{n.icon}</span> {n.label}
    {badge > 0 && <span className="ml-auto rounded-full bg-lime-500 px-1.5 text-[9px] font-bold text-slate-950">{badge}</span>}
  </button>
);

export default function Dashboard() {
  const [selectedDate, setSelectedDate] = useState(todayInput());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);
  const { user, stats, logout } = useAuth();
  const { tracked, remove, clear } = useTracker();
  const [trackerOpen, setTrackerOpen] = useState(false);
  React.useEffect(() => { window.scrollTo(0, 0); }, []);
  const [view, setView] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab") || sessionStorage.getItem("ht_next_tab");
    sessionStorage.removeItem("ht_next_tab");
    return NAV.some((n) => n.key === t && t !== "tracker") ? t : "home";
  });
  const summary = useSlateSummary(data?.date);
  const go = (key, gameKey) => {
    if (key === "tracker") { setTrackerOpen(true); return; }
    setView(key === "targets" ? "slate" : key);
    if (gameKey) {
      const g = (data?.games || []).find((x) => `${x.away.abbrev}@${x.home.abbrev}` === gameKey);
      if (g) setOpenId(g.espn_id);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const viewKey = view === "slate" ? "targets" : view;
  const meta = NAV_META[viewKey] || {};

  const load = useCallback(async (dateInput, isRefresh = false) => {
    isRefresh ? setRefreshing(true) : setLoading(true);
    setError(null);
    try {
      const params = dateInput ? { date: toApiDate(dateInput) } : {};
      const url = isRefresh ? `${API}/refresh` : `${API}/dashboard`;
      const res = isRefresh
        ? await axios.post(url, null, { params })
        : await axios.get(url, { params });
      setData(res.data);
      if (res.data?.date) setSelectedDate(toInputDate(res.data.date));
      if (isRefresh) toast.success("All data sources refreshed");
    } catch (e) {
      console.error(e);
      setError("Failed to load NHL data. Please try again.");
      if (isRefresh) toast.error("Refresh failed");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // initial load — let backend resolve the current slate
  useEffect(() => {
    load(null, false);
  }, [load]);

  const goDate = (input) => {
    setSelectedDate(input);
    load(input, false);
  };

  const games = data?.games || [];

  return (
    <PlayerCardProvider>
    <div className="min-h-screen grid-dots">

      {/* HERO HEADER */}
      <header className="sticky top-0 z-50 border-b border-cyan-900/40 bg-[#03050A]/95 backdrop-blur-xl">
        <div className="relative overflow-hidden">
          {/* hero artwork band */}
          <div className="relative mx-auto max-w-[1600px] px-4 sm:px-6">
            <div className="relative flex items-center justify-between gap-4 py-2">
              <div className="flex min-w-0 items-center gap-3">
                <img
                  src={HERO_IMG}
                  alt="QSI HAT TRICK — NHL Goal Target Intelligence"
                  data-testid="hero-logo"
                  className="h-12 w-auto rounded-md object-contain drop-shadow-[0_0_24px_rgba(34,211,238,0.35)] sm:h-14"
                  loading="eager"
                />
                <div className="hidden flex-col leading-none sm:flex">
                  <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.3em] text-lime-400">A QSI Product</span>
                  <span className="mt-1 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">NHL Goal Target Intelligence</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div data-testid="user-counts" className="hidden items-center gap-3 rounded-lg border border-lime-900/40 bg-lime-950/10 px-3 py-1.5 sm:flex">
                  <Users className="h-3.5 w-3.5 text-lime-400" />
                  <div className="leading-none">
                    <p className="font-mono text-[8px] uppercase tracking-widest text-slate-500">Registered</p>
                    <p data-testid="total-registered" className="font-mono text-sm font-bold text-lime-300">{NUMFMT(stats?.total_registered)}</p>
                  </div>
                  <div className="h-6 w-px bg-slate-800" />
                  <div className="leading-none">
                    <p className="font-mono text-[8px] uppercase tracking-widest text-slate-500">Active Now</p>
                    <p data-testid="active-now" className="font-mono text-sm font-bold text-emerald-300">{NUMFMT(stats?.active_now)}</p>
                  </div>
                </div>
                <span className="flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-950/50 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-300 shadow-[0_0_14px_rgba(16,185,129,0.25)]">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" /> Live
                </span>
                {user && (
                  <button data-testid="logout-btn" onClick={logout} title={user.email}
                    className="flex items-center gap-1.5 rounded-lg border border-slate-800 bg-slate-900/60 px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400 transition hover:border-rose-800 hover:text-rose-300">
                    <LogOut className="h-3.5 w-3.5" /> <span className="hidden md:inline">Logout</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* mobile grouped nav */}
          <div className="border-t border-cyan-900/30 lg:hidden">
            <div className="flex items-center gap-1 overflow-x-auto px-3 py-1.5" data-testid="mobile-nav">
              {SECTIONS.map((sec) => (
                <React.Fragment key={sec.title}>
                  <span className="shrink-0 px-1 font-mono text-[8px] uppercase tracking-[0.2em] text-slate-600">{sec.title}</span>
                  {sec.items.map((n) => <NavButton key={n.key} n={n} compact active={viewKey === n.key} badge={n.key === "tracker" ? tracked.length : 0} onClick={() => go(n.key)} />)}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* controls */}
          <div className="mx-auto max-w-[1600px] px-4 py-2.5 sm:px-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className={`flex flex-wrap items-center gap-2 ${view === "tennis" || view === "nba" ? "hidden" : ""}`}>
                <div className="flex items-center gap-1 rounded-lg border border-slate-800 bg-slate-900/60 p-1">
                  <button
                    data-testid="date-prev-btn"
                    onClick={() => goDate(shiftDate(selectedDate, -1))}
                    className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-800 hover:text-cyan-400"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <div className="flex items-center gap-1.5 px-1">
                    <CalendarDays className="h-3.5 w-3.5 text-cyan-500" />
                    <Input
                      data-testid="date-selector"
                      type="date"
                      value={selectedDate}
                      onChange={(e) => e.target.value && goDate(e.target.value)}
                      className="h-7 w-[140px] border-0 bg-transparent p-0 font-mono text-xs text-slate-200 focus-visible:ring-0"
                    />
                  </div>
                  <button
                    data-testid="date-next-btn"
                    onClick={() => goDate(shiftDate(selectedDate, 1))}
                    className="rounded p-1.5 text-slate-400 transition-colors hover:bg-slate-800 hover:text-cyan-400"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>

                <Button
                  data-testid="refresh-data-btn"
                  onClick={() => load(selectedDate, true)}
                  disabled={refreshing || loading}
                  className="h-9 gap-2 bg-gradient-to-r from-cyan-500 to-blue-500 font-mono text-xs font-bold uppercase tracking-wider text-slate-950 shadow-[0_0_18px_rgba(34,211,238,0.35)] hover:from-cyan-400 hover:to-blue-400"
                >
                  <RefreshCw className={`h-4 w-4 ${refreshing ? "spin" : ""}`} />
                  Refresh All Data
                </Button>
              </div>


            </div>

          </div>
        </div>
      </header>

      {/* CONTENT */}
      <div className="mx-auto flex max-w-[1600px] gap-6 px-4 sm:px-6">
      <aside className="sticky top-[120px] hidden h-[calc(100vh-130px)] w-56 shrink-0 overflow-y-auto py-6 lg:block" data-testid="sidebar-nav">
        {SECTIONS.map((sec) => (
          <div key={sec.title} className="mb-5">
            <p className="mb-1.5 px-3 font-mono text-[9px] font-bold uppercase tracking-[0.25em] text-slate-600">{sec.title}</p>
            <div className="space-y-0.5">
              {sec.items.map((n) => <NavButton key={n.key} n={n} active={viewKey === n.key} badge={n.key === "tracker" ? tracked.length : 0} onClick={() => go(n.key)} />)}
            </div>
          </div>
        ))}
      </aside>
      <main className="min-w-0 flex-1 py-6">
        <PageHeader title={meta.label} description={meta.desc} legend={meta.legend} testId={`page-header-${viewKey}`} />
        {view === "home" ? (
          <TonightHome games={games} summary={summary} onGo={go} />
        ) : view === "pairing" ? (
          <PositionPairingTab apiDate={data?.date} />
        ) : view === "live" ? (
          <LiveTrackerTab apiDate={data?.date} />
        ) : view === "archetypes" ? (
          <>
            <ArchetypeHitRates apiDate={data?.date} />
            <PairingTab apiDate={data?.date} />
          </>
        ) : view === "goalie" ? (
          <GoalieOriginTab apiDate={data?.date} />
        ) : view === "numerology" ? (
          <NumerologyTab apiDate={data?.date} />
        ) : view === "official" ? (
          <OfficialCardTab apiDate={data?.date} />
        ) : view === "lines" ? (
          <GameLinesTab apiDate={data?.date} />
        ) : view === "skaters" ? (
          <SkaterStatsTab />
        ) : view === "tennis" ? (
          <GreenBallTab />
        ) : view === "nba" ? (
          <FirstBucketTab />
        ) : view === "audit" ? (
          <AuditTab />
        ) : (
        <>
        {data?.date && (
          <div className="mb-4 flex items-center gap-2">
            <span className="font-display text-sm font-bold uppercase tracking-wide text-slate-300">
              {dateLabel(toInputDate(data.date) + "T12:00:00")}
            </span>
            <span className="font-mono text-xs text-slate-600">· {games.length} {games.length === 1 ? "game" : "games"}</span>
          </div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 text-slate-500">
            <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
            <p className="mt-4 font-mono text-xs uppercase tracking-widest">Pulling live NHL data…</p>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-32 text-slate-500" data-testid="error-state">
            <WifiOff className="h-8 w-8 text-rose-500" />
            <p className="mt-4 font-mono text-xs uppercase tracking-widest">{error}</p>
            <Button onClick={() => load(selectedDate, false)} className="mt-4 bg-slate-800 font-mono text-xs">Retry</Button>
          </div>
        ) : games.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-slate-500" data-testid="empty-state">
            <CalendarDays className="h-8 w-8 text-slate-700" />
            <p className="mt-4 font-mono text-xs uppercase tracking-widest">No NHL games scheduled for this date</p>
            <p className="mt-1 font-mono text-[10px] text-slate-600">Use the date selector to find a slate</p>
          </div>
        ) : (
          <div className="flex max-w-[1100px] flex-col gap-3" data-testid="games-grid">
            {games.map((g, i) => (
              <GameCard
                key={g.espn_id}
                game={g}
                index={i}
                isOpen={openId === g.espn_id}
                onToggle={() => setOpenId((cur) => (cur === g.espn_id ? null : g.espn_id))}
                summary={summary.byGame?.[`${g.away.abbrev}@${g.home.abbrev}`]}
              />
            ))}
          </div>
        )}
        </>
        )}
      </main>
      </div>

      {/* PLAYER TRACKER DRAWER */}
      {trackerOpen && (
        <div className="fixed inset-0 z-[60] flex justify-end" data-testid="tracker-drawer">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setTrackerOpen(false)} />
          <div className="relative flex h-full w-full max-w-md flex-col border-l border-cyan-900/40 bg-[#05070B] shadow-[0_0_60px_rgba(0,0,0,0.8)]">
            <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
              <div className="flex items-center gap-2">
                <Crosshair className="h-4 w-4 text-cyan-400" />
                <span className="font-display text-sm font-bold uppercase tracking-wide text-white">Player Tracker</span>
                <span className="rounded-full bg-lime-500 px-1.5 text-[10px] font-bold text-slate-950">{tracked.length}</span>
              </div>
              <button data-testid="tracker-close" onClick={() => setTrackerOpen(false)} className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="flex-1 space-y-2 overflow-y-auto p-4">
              {tracked.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-24 text-slate-600">
                  <Crosshair className="h-8 w-8" />
                  <p className="mt-3 font-mono text-[11px] uppercase tracking-widest">No tracked targets yet</p>
                  <p className="mt-1 font-mono text-[10px] text-slate-700">Tap "Track" on any goal target</p>
                </div>
              ) : tracked.map((t) => (
                <div key={t.id} data-testid={`tracked-item-${t.team}-${t.jersey}`} className="rounded-lg border border-slate-800 bg-slate-900/50 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-sm font-bold text-cyan-400">#{t.jersey}</span>
                        <span className="font-display text-sm font-bold uppercase text-slate-100">{t.name}</span>
                      </div>
                      <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-400">
                        {t.position} • {t.line}{t.pp_unit ? ` • ${t.pp_unit}` : ""} · {t.team}
                      </p>
                      <p className="mt-1 font-mono text-[10px] text-amber-300">
                        vs {t.opponent} {t.position} {t.opp_ga_gp ?? "—"} GA/GP
                        {t.goalie_name && <span className="text-lime-500/80"> · G:{String(t.goalie_name).split(" ").slice(-1)[0]} {t.goalie_ga ?? "—"}GA</span>}
                      </p>
                    </div>
                    <button onClick={() => remove(t.id)} className="rounded p-1 text-slate-500 hover:bg-slate-800 hover:text-rose-400">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            {tracked.length > 0 && (
              <div className="border-t border-slate-800 p-3">
                <button onClick={clear} className="w-full rounded-lg border border-slate-800 py-2 font-mono text-[11px] font-bold uppercase tracking-wider text-slate-400 hover:border-rose-800 hover:text-rose-300">
                  Clear All
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      <AIAssistant apiDate={data?.date} />
    </div>
    </PlayerCardProvider>
  );
}
